import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import KeyEnum = Boo.Objects.Enums.KeyEnum;
import { catchError, finalize, of, map, Observable, throwError, forkJoin } from 'rxjs';
import TaskTypeEnum = Boo.Objects.Enums.TaskTypeEnum;
import { SaveTypes } from 'app/models/enums/SaveTypes';
import { WorkflowKeyValuePairService } from '../../../../../../services/workflow-keyvaluepair.service';
import WorkflowHelper from '../../../../../../shared/WorkflowHelper';
import CustomValidators from '../../../../../../shared/custom-validators';
import { CustomerCampaignService } from '../../../../../../services/customer-campaign.service';
import Task = Boo.Objects.Task;

@Component({
  selector: 'app-components-specialist-actioncomponents-page-optimization-shared-implementation-url',
  templateUrl: './implementation-url.component.html'
})
export class PageOptimizationImplementationUrlComponent implements OnInit {
  @Input() orderingTask: Task;
  @Input() workflowId: number;
  @Input() websiteUrlId: number;
  @Input() customerId: number;
  @Input() customerUrl: string;
  @Input() isReadOnly = false;

  formGroup: FormGroup;
  pageUrlControl: FormControl<string>;

  isLoading = true;
  isRequired = false;
  workflowKeyValuePairs: Boo.Objects.WorkflowKeyValuePair[] = [];
  otherExistingUrls: string[] = [];
  pageUrl: string;

  constructor(
    private customerCampaignService: CustomerCampaignService,
    private workflowKeyValuePairService: WorkflowKeyValuePairService) { }

  ngOnInit(): void {

    this.isRequired = this.orderingTask.TaskTypeEnum === TaskTypeEnum.PageCreation;

    if (this.isRequired) {
      forkJoin([
        this.workflowKeyValuePairService.getByWorkflowId(this.workflowId),
        this.isReadOnly ? of(null) : this.customerCampaignService.getWebsiteUrls(this.customerId)
      ])
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: ([workflowKeyValuePairs, websiteUrls]) => {
          this.workflowKeyValuePairs = workflowKeyValuePairs;
          this.pageUrl = WorkflowHelper.get(workflowKeyValuePairs, KeyEnum.ImplementationUrl) || '';
          this.otherExistingUrls = websiteUrls ? websiteUrls.filter(websiteUrl => websiteUrl.WebsiteUrlId !== this.websiteUrlId).map(websiteUrl => websiteUrl.Url.replace(this.customerUrl, '')) : [];
          this.setupForm();
        },
        error: (err) => toastr.error('Failed to load audit:', err)
      });
    }
  }

  // This form does not have to be valid for SaveTypes.Save
  isValid(saveType): boolean {
    if (this.isReadOnly || !this.isRequired) {
      return true;
    }

    this.validateAllControls(false);

    if (!this.formGroup.valid && saveType !== SaveTypes.Save) {
      this.validateAllControls(true);
      return false;
    }

    return true;
  }

  save(saveType: SaveTypes): Observable<void> {
    if (this.isReadOnly || !this.isValid(saveType) || !this.isRequired) {
      return of(void 0);
    }

    this.pageUrl = this.pageUrlControl.value.trim();

    WorkflowHelper.setValue(this.workflowKeyValuePairs, this.workflowId, KeyEnum.ImplementationUrl, this.pageUrl);
    return this.workflowKeyValuePairService.save([WorkflowHelper.getWorkflowKeyValuePair(this.workflowKeyValuePairs, KeyEnum.ImplementationUrl)])
      .pipe(
        map(() => void 0),
        catchError(err => {
          toastr.error('Failed to save New Page Url:', err);
          return throwError(() => err);
        })
      );
  }

  private setupForm(): void {
    this.pageUrlControl = new FormControl(this.pageUrl, [
      Validators.required,
      CustomValidators.validUrl(this.customerUrl),
      CustomValidators.stringsNotAllowed(this.otherExistingUrls, true, 'This URL is already in use.'),
      CustomValidators.domainMustMatchPrefix(this.customerUrl, this.customerUrl, `Domain must match the customer's domain.`),
      CustomValidators.maxLengthPrefix(this.customerUrl, 512)
    ]);
    this.formGroup = new FormGroup({
      pageUrlControl: this.pageUrlControl
    });
  }

  private validateAllControls(emitEvent: boolean) : void {
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.controls[key].markAsDirty();
      this.formGroup.controls[key].updateValueAndValidity({ emitEvent: emitEvent });
    });
  }
}
